<template>
    <div class="live-notifications">
        <!-- This component is used for real-time notifications via Echo -->
    </div>
</template>

<script>
import debounce from 'lodash/debounce.js';
import { mapState } from 'vuex';

import { BELL_REFRESH_EVENT } from '@/components/notifications/bellRefresh.js';
import BellNotification from '@/models/BellNotification.js';

/**
 * Broadcasts arrive in bursts (marking several items read sends one event each), so the bell
 * refreshes once the burst settles instead of once per event.
 */
export const REFRESH_DEBOUNCE_MS = 1000;

/** A steady stream of broadcasts still refreshes the bell at least this often. */
export const REFRESH_MAX_WAIT_MS = 5000;

export default {
    name: 'LiveNotifications',

    computed: {
        ...mapState('Auth', { user: state => state.user }),

        echoReady() {
            return !!this.$store.state.Online.$echo;
        },

        page() {
            return this.$store.state.entities.bell_notifications.page;
        },
    },

    watch: {
        echoReady: {
            handler(ready) {
                if (ready) {
                    this.$echo
                        .private(`notifications-${this.user.id}`)
                        // New notifications arriving.
                        .listen('.notification', () => this.scheduleRefresh())
                        // Read/unread changes (e.g. marking read from the inbox) must also
                        // refresh the bell counter live, not only after a page reload.
                        .listen('ModelReadStatusChanged', () => this.scheduleRefresh());
                }
            },
            immediate: true,
        },
    },
    created() {
        this.debouncedRefresh = debounce(() => BellNotification.fetch(this.page), REFRESH_DEBOUNCE_MS, {
            maxWait: REFRESH_MAX_WAIT_MS,
        });
    },

    mounted() {
        BellNotification.fetch(this.page);
        // A change made in this tab refreshes the bell at once; other tabs and devices still get the broadcast.
        window.addEventListener(BELL_REFRESH_EVENT, this.reloadAfterReadChange);
    },

    beforeUnmount() {
        this.debouncedRefresh?.cancel();
        window.removeEventListener(BELL_REFRESH_EVENT, this.reloadAfterReadChange);
    },

    methods: {
        scheduleRefresh() {
            this.debouncedRefresh();
        },

        async fetchNotifications() {
            await BellNotification.fetch(this.page);
        },

        /**
         * The bell lists unread items only and a fetch merges into the store, so an item read in the Inbox
         * would linger in the drawer as unread. Start from the first page of what is still unread.
         */
        async reloadAfterReadChange() {
            await BellNotification.deleteAll();
            // Only page 1 is reloaded, so the drawer's paging must restart there too.
            await BellNotification.commit(state => (state.page = 1));
            await BellNotification.fetch(1);
        },
    },
};
</script>

<style scoped>
.live-notifications {
    display: none; /* Hidden component used only for real-time functionality */
}
</style>
